import { useMemo } from 'react';

import keyBy from 'lodash/keyBy';

import { Avatar, AvatarProps } from '@cast/design-system';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import {
  getOrganizationColorIndex,
  getOrganizationAvatarStyles,
  getOrganizationDisplayName,
} from './organizationAvatarUtils';

type OrganizationAvatarProps = {
  organizationId?: string;
  placeholderName?: string;
} & Pick<AvatarProps, 'size'>;

export const OrganizationAvatar = ({
  size = 'xs',
  organizationId,
  placeholderName = '',
}: OrganizationAvatarProps) => {
  /**
   * Using organization values from OrganizationProvider as some API responses
   * don't have properties we calculate color index from for example:
   * createdAt is missing in ListMembersResponseOrganization
   * BillingV1OrganizationUsage has completely different structure
   */
  const { organizations } = useOrganizations();
  const orgsById = useMemo(() => keyBy(organizations, 'id'), [organizations]);
  const organization = organizationId ? orgsById[organizationId] : undefined;
  const orgName = getOrganizationDisplayName(organization, placeholderName);
  const colorIndex = organization ? getOrganizationColorIndex(organization) : 0;
  const sx = getOrganizationAvatarStyles(organization, colorIndex);

  return (
    <Avatar
      text={orgName}
      sx={{ ...sx, '&:hover': sx }}
      size={size}
      variant="square"
    />
  );
};
